import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { routeDonut } from "@/setup/routes/catalog.routes";
import { autocompleteOptionColor } from "@/utils/theme";
import { ErrorModal } from "@dexteel/mesf-core";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
	Autocomplete,
	Box,
	Chip,
	CircularProgress,
	IconButton,
	TextField,
	Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Donut } from "../models/Donut";
import { getDonuts } from "../repositories/DonutRepository";
import { NewDonutDialog } from "./NewDonutDialog";

type Props = {
	label: string;
	value: number | null;
	onChange?: Function;
	disabled?: boolean;
	onForceAssembly?: (force: boolean) => void;
	sectionFamilyIds?: string | null;
};

const useDonuts = (partId?: number, onError?: (error: Error) => void) => {
	return useQuery<Donut[], Error>({
		queryKey: ["donuts", partId],
		meta: { realtime: ["M2.GetDonuts"] },
		queryFn: ({ signal }) => getDonuts({ partId }, signal),
		onError: onError,
	});
};

export const DonutSelector = ({
	label,
	value,
	onChange = () => {},
	disabled = false,
	onForceAssembly = () => {},
	sectionFamilyIds,
}: Props) => {
	const [error, setError] = useState("");
	const [message, setMessage] = useState("");
	const [selectedValue, setSelectedValue] = useState<Donut | null>(null);
	const [showNewDonutDialog, setShowNewDonutDialog] = useState(false);

	const { data: donuts, isLoading } = useDonuts(undefined, (error) =>
		setError(error.message),
	);

	const selectedDonut = donuts?.find((donut) => donut.PartId === value) || null;

	const inUse = (props: Donut | null) => {
		if (props?.InUse) return true;
		return false;
	};

	const handleConfirmationClose = (result?: string) => {
		setMessage("");
		if (result === "OK") {
			onForceAssembly(true);
			onChange(selectedValue?.PartId ?? null);
			setSelectedValue(null);
			return;
		}
	};

	const getOptionsToShow = () => {
		return (
			donuts
				?.filter((d) => {
					const isReady = d.DonutStatusCode === "R";
					if (!sectionFamilyIds) {
						return isReady;
					}

					return isReady && (!inUse(d) || d?.PartId == value);
				})
				.sort((a, b) => {
					if (!sectionFamilyIds) return 0;

					const sectionFamilyIdList = sectionFamilyIds
						.split(",")
						.map((id) => id.trim());

					const getPriority = (donut: Donut) => {
						if (donut.SectionFamilyId == null) return 0;
						if (sectionFamilyIdList.includes(donut.SectionFamilyId?.toString()))
							return 1;
						return 2;
					};

					return getPriority(a) - getPriority(b);
				}) || []
		);
	};

	return (
		<>
			<Autocomplete
				clearOnEscape
				autoHighlight
				options={getOptionsToShow()}
				getOptionLabel={(option) => option.PartName}
				renderOption={(props, option) => {
					return (
						<Box
							{...props}
							component="li"
							bgcolor={inUse(option) ? autocompleteOptionColor : ""}
						>
							<Box
								sx={{
									width: "100%",
									height: "100%",
									boxSizing: "border-box",
								}}
							>
								<span>
									<Typography>
										{option.PartName}
										<i>
											{inUse(option)
												? option?.PartId == value
													? " (Actual) "
													: " (In Use)"
												: ""}
										</i>
									</Typography>
								</span>
								<div
									style={{
										display: "flex",
										width: "100%",
										gap: "8px",
										marginTop: 7,
									}}
								>
									<Chip
										size="small"
										color="primary"
										variant="outlined"
										label={`Section Family: ${
											option?.SectionFamilyName ?? "-"
										}`}
									/>
								</div>
							</Box>
						</Box>
					);
				}}
				isOptionEqualToValue={(option, value) => option.PartId === value.PartId}
				disabled={disabled}
				renderInput={(params) => (
					<TextField
						{...params}
						label={label}
						inputProps={{
							...params.inputProps,
							"data-test-id": "donut-selector",
						}}
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									{isLoading ? (
										<CircularProgress color="inherit" size={20} />
									) : (
										<>
											{params.InputProps.endAdornment}
											{!disabled && (
												<IconButton
													onClick={(e) => {
														e.stopPropagation();
														setShowNewDonutDialog(true);
													}}
													size="small"
													title="Add new donut"
													data-test-id="add-new-donut-button"
												>
													<AddIcon fontSize="small" />
												</IconButton>
											)}
											{value ? (
												<IconButton
													component={Link}
													to={`${routeDonut}/${value}`}
													target="_blank"
													size="small"
													title="See in donuts"
												>
													<VisibilityIcon fontSize="small" />
												</IconButton>
											) : null}
										</>
									)}
								</>
							),
						}}
					/>
				)}
				value={selectedDonut}
				onChange={(event, newValue) => {
					if (inUse(newValue)) {
						setMessage(
							`The donut ${newValue?.PartName} is already in use by another roll, do you want to disassemble it?`,
						);
						setSelectedValue(newValue);
					} else onChange(newValue?.PartId ?? null);
				}}
			/>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error loading donuts"
			/>
			<ConfirmationDialogRaw
				title="CONFIRMATION"
				message={message}
				open={message !== ""}
				onClose={handleConfirmationClose}
			/>
			<NewDonutDialog
				show={showNewDonutDialog}
				onHide={(newPartId?: number) => {
					setShowNewDonutDialog(false);
					if (newPartId) {
						onChange(newPartId);
					}
				}}
			/>
		</>
	);
};
